import { FunnelChart, type FunnelStage } from '../fab-ui';
import { PiSection, PiStat, PiStatGrid, piCount, piPct } from '../pi-kit';
import { icrRate, monthsCoveredText, monthsSpanText, type IcrViewOverall } from './icr-model';

/**
 * ICR headline: four tiles, then the outcome funnel.
 *
 * Tiles: Universities (representatives) · Reports (months covered) · Leads
 * generated · Enrolled (share of leads). Funnel: Leads generated → Engaged →
 * Interested next intake → Applications submitted → Admitted → Offers accepted
 * → Enrolled. Every figure is the server's `overall` block, copied, never
 * summed here; a missing figure renders an em dash.
 *
 * Layout manager (2026-10-20): the page renders {@link IcrStats} fixed and
 * {@link IcrOutcomes} as a movable block beside the university table.
 */
interface IcrOverviewProps {
  overall: IcrViewOverall | null;
  loading: boolean;
}

export function IcrStats({ overall, loading }: IcrOverviewProps) {
  const pending = loading && overall === null;

  const representatives = overall?.representatives ?? null;
  const months =
    overall === null ? null : monthsSpanText(overall.monthsCovered) ?? monthsCoveredText(overall.monthsCovered);
  const enrolledRate = icrRate(overall?.enrolled ?? null, overall?.leadsGenerated ?? null);

  return (
    <PiStatGrid>
      <PiStat
        label="Universities"
        value={piCount(overall?.universities)}
        loading={pending}
        detail={
          representatives !== null
            ? `${piCount(representatives)} ${representatives === 1 ? 'representative' : 'representatives'}`
            : undefined
        }
      />
      <PiStat label="Reports" value={piCount(overall?.reports)} loading={pending} detail={months ?? undefined} />
      <PiStat label="Leads generated" value={piCount(overall?.leadsGenerated)} loading={pending} />
      <PiStat
        label="Enrolled"
        value={piCount(overall?.enrolled)}
        loading={pending}
        detail={enrolledRate !== null ? `${piPct(enrolledRate)} of leads` : undefined}
      />
    </PiStatGrid>
  );
}

export function IcrOutcomes({ overall, loading }: IcrOverviewProps) {
  const pending = loading && overall === null;

  const stages: FunnelStage[] = [
    { key: 'generated', label: 'Leads generated', value: overall?.leadsGenerated ?? null },
    { key: 'engaged', label: 'Engaged', value: overall?.leadsEngaged ?? null },
    { key: 'interested', label: 'Interested next intake', value: overall?.interestedNextIntake ?? null },
    { key: 'submitted', label: 'Applications submitted', value: overall?.applicationsSubmitted ?? null },
    { key: 'admitted', label: 'Admitted', value: overall?.admitted ?? null },
    { key: 'accepted', label: 'Offers accepted', value: overall?.offersAccepted ?? null },
    { key: 'enrolled', label: 'Enrolled', value: overall?.enrolled ?? null },
  ];

  return (
    <PiSection title="Outcomes">
      <FunnelChart stages={stages} loading={pending} />
    </PiSection>
  );
}
